import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Refresh,
  Download,
  LocalGasStation,
  Speed,
  Build,
  Schedule
} from '@mui/icons-material';
import apiClient from '../../api';

const VehicleStatsChart = ({ vehicleId, vehicleName }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30'); // days
  const [metricType, setMetricType] = useState('all'); // all, fuel, distance, engine, idle
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (vehicleId) {
      loadVehicleStats();
    }
  }, [vehicleId, dateRange]);

  const loadVehicleStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(dateRange));

      const response = await apiClient.get(
        `/samsara/vehicles/${vehicleId}/stats?startTime=${startDate.toISOString()}&endTime=${endDate.toISOString()}`
      );
      
      setStats(response.data);
      
      // Check if stats are empty (all zeros) - this means the endpoint is not available
      // Don't show an error, just don't display stats (the component will handle this gracefully)
      if (response.data && 
          (!response.data.fuelUsed || response.data.fuelUsed.value === 0) &&
          (!response.data.distance || response.data.distance.value === 0) &&
          (!response.data.engineHours || response.data.engineHours.value === 0)) {
        // Stats not available, but don't show error - just set stats to null
        setStats(null);
      }
    } catch (err) {
      // Only log if it's a real server error (500+)
      // Backend now returns empty stats instead of errors, so most cases won't hit this
      if (err.response && err.response.status >= 500) {
        console.error('Error loading vehicle stats:', err);
        setError('Failed to load vehicle statistics');
      } else {
        // Stats not available - don't show error, just don't display stats
        setStats(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadVehicleStats();
    setRefreshing(false);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return Number(num).toLocaleString();
  };

  const formatValue = (value, unit) => {
    if (value === null || value === undefined) return 'N/A';
    return `${formatNumber(value)} ${unit || ''}`;
  };

  const getTrendIcon = (current, previous) => {
    if (!current || !previous) return null;
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return null;
  };

  const getTrendColor = (current, previous) => {
    if (!current || !previous) return 'default';
    if (current > previous) return 'success';
    if (current < previous) return 'error';
    return 'default';
  };

  const getMetricCards = () => {
    if (!stats) return [];

    const metrics = [
      {
        key: 'fuelUsed',
        label: 'Fuel Used',
        value: stats.fuelUsed?.value,
        unit: stats.fuelUsed?.unit || 'gallons',
        icon: <LocalGasStation />,
        color: 'primary'
      },
      {
        key: 'distance',
        label: 'Distance',
        value: stats.distance?.value,
        unit: stats.distance?.unit || 'miles',
        icon: <Speed />,
        color: 'primary'
      },
      {
        key: 'engineHours',
        label: 'Engine Hours',
        value: stats.engineHours?.value,
        unit: stats.engineHours?.unit || 'hours',
        icon: <Build />,
        color: 'primary'
      },
      {
        key: 'idleTime',
        label: 'Idle Time',
        value: stats.idleTime?.value,
        unit: stats.idleTime?.unit || 'hours',
        icon: <Schedule />,
        color: 'warning'
      }
    ];

    return metrics.filter(metric => 
      metricType === 'all' || metric.key === metricType
    );
  };

  const getHistoricalData = () => {
    if (!stats || !stats.historicalData) return [];

    return stats.historicalData.map(point => ({
      date: new Date(point.timestamp).toLocaleDateString(),
      fuel: point.fuelUsed || 0,
      distance: point.distance || 0,
      engineHours: point.engineHours || 0,
      idleTime: point.idleTime || 0
    }));
  };

  const exportData = () => {
    if (!stats) return;

    const data = {
      vehicleId,
      vehicleName,
      dateRange,
      generatedAt: new Date().toISOString(),
      summary: {
        fuelUsed: stats.fuelUsed,
        distance: stats.distance,
        engineHours: stats.engineHours,
        idleTime: stats.idleTime
      },
      historicalData: getHistoricalData()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vehicle-stats-${vehicleId}-${dateRange}days.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    // Don't show anything if stats aren't available - just return null
    // This prevents showing an error message when stats simply aren't available
    return null;
  }

  const metricCards = getMetricCards();

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Vehicle Statistics: {vehicleName || `Vehicle ${vehicleId}`}
          </Typography>
          <Box display="flex" gap={1}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Metric Type</InputLabel>
              <Select
                value={metricType}
                label="Metric Type"
                onChange={(e) => setMetricType(e.target.value)}
              >
                <MenuItem value="all">All Metrics</MenuItem>
                <MenuItem value="fuel">Fuel Usage</MenuItem>
                <MenuItem value="distance">Distance</MenuItem>
                <MenuItem value="engine">Engine Hours</MenuItem>
                <MenuItem value="idle">Idle Time</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} disabled={refreshing}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Data">
              <IconButton onClick={exportData}>
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Metric Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {metricCards.map((metric) => (
            <Grid item xs={12} sm={6} md={3} key={metric.key}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {metric.icon}
                    <Typography variant="subtitle2" color="textSecondary">
                      {metric.label}
                    </Typography>
                  </Box>
                  <Typography variant="h5" color={`${metric.color}.main`}>
                    {formatValue(metric.value, metric.unit)}
                  </Typography>
                  {stats.previousPeriod && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      {getTrendIcon(metric.value, stats.previousPeriod[metric.key]?.value)}
                      <Chip
                        label={`vs previous period`}
                        color={getTrendColor(metric.value, stats.previousPeriod[metric.key]?.value)}
                        size="small"
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Performance Summary */}
        {stats.summary && (
          <Box mb={3}>
            <Typography variant="subtitle1" gutterBottom>
              Performance Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Average Speed
                  </Typography>
                  <Typography variant="h6">
                    {formatValue(stats.summary.avgSpeed, 'mph')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Fuel Efficiency
                  </Typography>
                  <Typography variant="h6">
                    {formatValue(stats.summary.fuelEfficiency, 'mpg')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Idle Percentage
                  </Typography>
                  <Typography variant="h6">
                    {formatValue(stats.summary.idlePercentage, '%')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Utilization Rate
                  </Typography>
                  <Typography variant="h6">
                    {formatValue(stats.summary.utilizationRate, '%')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Historical Data Table */}
        {getHistoricalData().length > 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Historical Data
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5' }}>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>
                      Date
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>
                      Fuel Used
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>
                      Distance
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>
                      Engine Hours
                    </th>
                    <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd' }}>
                      Idle Time
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getHistoricalData().map((row, index) => (
                    <tr key={index}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {row.date}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {formatNumber(row.fuel)} gallons
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {formatNumber(row.distance)} miles
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {formatNumber(row.engineHours)} hours
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {formatNumber(row.idleTime)} hours
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

        {/* No Data Message */}
        {metricCards.length === 0 && (
          <Alert severity="info">
            No data available for the selected metric type and date range.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default VehicleStatsChart;
